// src/components/RestoreAccountPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { routes } from '../../constants';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import StyledPage from '../../components/StyledPage';
import axios from 'axios';

export default function RestoreAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  useEffect(() => {
    async function restoreAccount() {
      if (!token) {
        setErrorMessage(<FormattedMessage id="restore_account.no_token" />);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.post('/api/v1/accounts/restore-account/', { token });
        if (response.status === 200) {
          setSuccess(true);
        }
      } catch (e) {
        const errorDetail = e.response?.data?.detail || 'An unexpected error occurred';
        if (e.response?.status === 400) {
          setErrorMessage(<FormattedMessage id="restore_account.invalid_token" />);
        } else if (e.response?.status === 404) {
          setErrorMessage(<FormattedMessage id="restore_account.user_not_found" />);
        } else if (e.response?.status === 410) {
          setErrorMessage(<FormattedMessage id="restore_account.expired" />);
        } else {
          setErrorMessage(errorDetail);
        }
      } finally {
        setIsLoading(false);
      }
    }

    restoreAccount();
  }, [token]);

  return (
    <StyledPage
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'center' : 'space-between',
        alignItems: 'center',
        background: isMobile
          ? '#f5f5f5'
          : 'url("https://images.unsplash.com/photo-1507521628349-dee6b8290f6f")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box
        sx={{
          flex: isMobile ? 'auto' : 1,
          p: isMobile ? 2 : 5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: isMobile ? 'none' : 'blur(5px)',
          borderRadius: 1,
          maxWidth: isMobile ? '100%' : '500px',
          mx: 'auto',
          my: isMobile ? 0 : { xs: 1, md: 0 },
          boxShadow: isMobile ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 0,
          transform: isMobile ? 'translateY(-10%)' : 'none',
        }}
      >
        {isMobile && (
          <Typography
            variant="h5"
            sx={{ mb: 3, color: '#00438E', textAlign: 'center', fontWeight: 'bold' }}
          >
            PromptaAI
          </Typography>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <CircularProgress sx={{ color: '#00438E' }} />
          </Box>
        ) : success ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#00438E', fontWeight: 'bold' }}>
              <FormattedMessage id="restore_account.success_header" />
            </Typography>
            <Typography sx={{ mb: 3, color: '#00438E', fontSize: 16 }}>
              <FormattedMessage id="restore_account.success_message" />
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(routes.login)}
              sx={{
                width: isMobile ? '100%' : 'auto',
                backgroundColor: '#00438E',
                '&:hover': { backgroundColor: '#003A75' },
                py: 1.5,
                px: 3,
              }}
            >
              <FormattedMessage id="login.return_to_login" />
            </Button>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#00438E', fontWeight: 'bold' }}>
              <FormattedMessage id="error.unknown_error" />
            </Typography>
            <Typography sx={{ mb: 3, color: 'crimson', fontSize: 16 }}>
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(routes.login)}
              sx={{
                width: isMobile ? '100%' : 'auto',
                backgroundColor: '#00438E',
                '&:hover': { backgroundColor: '#003A75' },
                py: 1.5,
                px: 3,
              }}
            >
              <FormattedMessage id="login.return_to_login" />
            </Button>
          </Box>
        )}
      </Box>
    </StyledPage>
  );
}
